import React from 'react';
import styled from 'styled-components';
import useLocale from '../hooks/useLocale';

/*
 * PanchangamHeader — Section 1 of Pambu Sheet.
 *
 * Refactored for #194: removed inline FestivalBanner; festival now appears as
 * an inline mini-badge appended to the date line. Gold-accent and badge styling
 * inlined (no longer importing the deleted panchangamColors module).
 */

const HeaderContainer = styled.header`
  text-align: center;
  margin-bottom: ${p => p.theme.spacing[5]}px;
`;

const GoldAccent = styled.div`
  width: 80px;
  height: 4px;
  background: ${p => p.theme.colors.gold?.primary || p.theme.colors.bands.exceptional};
  margin: 0 auto ${p => p.theme.spacing[3]}px;
  border-radius: 2px;

  @media print { display: none; }
`;

const TamilDateLine = styled.div`
  font-size: 16px;
  font-weight: ${p => p.theme.typography.weights.semibold};
  color: ${p => p.theme.colors.gold?.primary || p.theme.colors.bands.exceptional};
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  @media print { color: #000; }
`;

const GregorianDateLine = styled.div`
  font-size: 14px;
  color: ${p => p.theme.colors.text.secondary};
  margin-top: ${p => p.theme.spacing[1]}px;
  @media print { color: #000; }
`;

const LocationLine = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.text.muted};
  margin-top: ${p => p.theme.spacing[1]}px;
  @media print { color: #000; }
`;

const SiddhantaBadge = styled.span`
  display: inline-block;
  margin-top: ${p => p.theme.spacing[2]}px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: ${p => p.theme.typography.weights.medium};
  border-radius: 10px;
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text.secondary};
  border: 1px solid ${p => p.theme.colors.border};
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
`;

const FestivalBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: ${p => p.theme.typography.weights.medium};
  border-radius: 12px;
  background: ${p => p.theme.colors.festival?.bg || 'rgba(184,134,11,0.10)'};
  color: ${p => p.theme.colors.festival?.text || p.theme.colors.bands.exceptional};
  border: 1px solid ${p => p.theme.colors.festival?.border || p.theme.colors.bands.exceptional};
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
`;

function formatDate(dateStr, lang) {
  const d = new Date(dateStr + 'T00:00:00');
  const locale = lang === 'ta' ? 'ta-IN' : 'en-IN';
  return d.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const PanchangamHeader = ({ header, festivals, lang = 'en', siddhanta }) => {
  const tSiddhanta = useLocale(lang, 'siddhanta');

  if (!header) return null;

  const tc = header.tamil_calendar;
  const tamilDate = tc
    ? lang === 'ta'
      ? `${tc.year_name_ta || tc.year_name} — ${tc.month_name_ta || tc.month_name} ${tc.date} (${tc.pirai_ta || tc.pirai})`
      : `${tc.year_name} — ${tc.month_name} ${tc.date} (${tc.pirai})`
    : null;

  const weekday = lang === 'ta' ? (header.weekday_ta || header.weekday) : header.weekday;
  const gregorian = `${weekday}, ${formatDate(header.date, lang)}`;

  const lat = header.latitude;
  const lng = header.longitude;
  const location = `${lat?.toFixed(4)}°N, ${lng?.toFixed(4)}°E — ${header.timezone}`;

  const badgeText = siddhanta && siddhanta !== 'compare'
    ? tSiddhanta?.badge?.[siddhanta]
    : null;

  const todayFestivals = festivals?.festivals || [];
  const firstFestival = todayFestivals[0];
  const festivalName = firstFestival
    ? (lang === 'ta' ? (firstFestival.name_ta || firstFestival.name) : firstFestival.name)
    : null;

  return (
    <HeaderContainer>
      <GoldAccent />
      {tamilDate && (
        <TamilDateLine>
          <span>{tamilDate}</span>
          {festivalName && <FestivalBadge>🎉 {festivalName}</FestivalBadge>}
        </TamilDateLine>
      )}
      <GregorianDateLine>{gregorian}</GregorianDateLine>
      <LocationLine>{location}</LocationLine>
      {badgeText && <SiddhantaBadge>{badgeText}</SiddhantaBadge>}
    </HeaderContainer>
  );
};

export default PanchangamHeader;
